/**
 * Loader for bundle format v2. Works in the browser (fetch + DecompressionStream)
 * and in Node (fs + zlib) through two small ports, so the pipeline's own
 * round-trip gate and the viewer use the same code the headset runs.
 */
import { isManifest, type ChunkRecord, type Manifest } from "./format.js";

export interface LoaderPorts {
  /** Fetch bytes at a URL (absolute or relative to the bundle base). */
  fetchBytes: (url: string) => Promise<ArrayBuffer>;
  /** Inflate a gzip stream. */
  gunzip: (bytes: ArrayBuffer) => Promise<ArrayBuffer>;
}

const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;

export function isGzip(bytes: ArrayBuffer): boolean {
  const u8 = new Uint8Array(bytes);
  return u8.length > 2 && u8[0] === GZIP_MAGIC_0 && u8[1] === GZIP_MAGIC_1;
}

/** Browser ports: `fetch` and `DecompressionStream`. */
export function browserPorts(fetchImpl: typeof fetch = fetch): LoaderPorts {
  return {
    async fetchBytes(url) {
      const response = await fetchImpl(url);
      if (!response.ok) throw new Error(`fetch ${url}: ${response.status}`);
      return response.arrayBuffer();
    },
    async gunzip(bytes) {
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
      return new Response(stream).arrayBuffer();
    },
  };
}

export function joinUrl(base: string, path: string): string {
  if (/^[a-z]+:\/\//i.test(path) || path.startsWith("/")) return path;
  return base.endsWith("/") ? base + path : `${base}/${path}`;
}

export async function loadManifest(base: string, ports: LoaderPorts): Promise<Manifest> {
  const bytes = await ports.fetchBytes(joinUrl(base, "manifest.json"));
  const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
  if (!isManifest(parsed)) throw new Error(`not a v2 manifest: ${joinUrl(base, "manifest.json")}`);
  return parsed;
}

/**
 * Load one chunk. Prefers the gzip file when the manifest lists one; if the
 * host already decoded it (Content-Encoding), the magic bytes say so and no
 * second inflate is attempted. Returns a buffer of exactly `chunk.bytes` bytes.
 */
export async function loadChunk(base: string, chunk: ChunkRecord, ports: LoaderPorts): Promise<ArrayBuffer> {
  let bytes: ArrayBuffer;
  if (chunk.gz) {
    bytes = await ports.fetchBytes(joinUrl(base, chunk.gz));
    if (isGzip(bytes)) bytes = await ports.gunzip(bytes);
  } else {
    bytes = await ports.fetchBytes(joinUrl(base, chunk.url));
  }
  if (bytes.byteLength !== chunk.bytes) {
    throw new Error(`chunk ${chunk.url}: expected ${chunk.bytes} bytes, got ${bytes.byteLength}`);
  }
  return bytes;
}

/** Load every chunk the given level needs, in manifest order. */
export async function loadLevelChunks(
  base: string,
  manifest: Manifest,
  level: string,
  ports: LoaderPorts,
  onProgress?: (loaded: number, total: number) => void,
): Promise<Map<number, ArrayBuffer>> {
  const wanted = manifest.chunks.map((c, i) => [c, i] as const).filter(([c]) => c.level === level);
  const out = new Map<number, ArrayBuffer>();
  let done = 0;
  await Promise.all(
    wanted.map(async ([chunk, index]) => {
      out.set(index, await loadChunk(base, chunk, ports));
      done += 1;
      onProgress?.(done, wanted.length);
    }),
  );
  return out;
}
